import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from '../../../lib/api-client';
import { WS_EVENTS } from '../../../lib/websocket/events';
import { useWebSocket } from '../../../lib/websocket/WebSocketProvider';

import type { ConductionProposalWire } from '../../../lib/api-client';

export const PROPOSALS_QUERY_KEY = ['conduction-proposals', 'mine'] as const;

/**
 * useMyProposals — Item 368 F2 Fatia D.
 *
 * Lista propostas de condução H_c pendentes das conversas atribuídas ao caller.
 * Invalida a query via WS `CONDUCTION_PROPOSAL_CREATED` para feedback imediato.
 *
 * `enabled` controla se a query dispara (gateia fetch, não só render).
 * Para o Painel Pessoal do atendente: `enabled=true` sempre (todo atendente
 * pode ter propostas; a cap gate real é no backend).
 */
export function useMyProposals(enabled: boolean): {
  proposals: ConductionProposalWire[];
  isLoading: boolean;
} {
  const queryClient = useQueryClient();
  const { subscribe } = useWebSocket();

  const q = useQuery({
    queryKey: PROPOSALS_QUERY_KEY,
    queryFn: () => api.myConductionProposals(),
    enabled,
    staleTime: 30_000,
  });

  // Invalida a query quando o backend emite CONDUCTION_PROPOSAL_CREATED via WS.
  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribe(WS_EVENTS.CONDUCTION_PROPOSAL_CREATED, () => {
      void queryClient.invalidateQueries({ queryKey: PROPOSALS_QUERY_KEY });
    });
    return unsub;
  }, [enabled, queryClient, subscribe]);

  return {
    proposals: q.data?.items ?? [],
    isLoading: q.isLoading,
  };
}
